import React, { useContext, useEffect, useState } from 'react'
import styles from './CompanyNameWithPeriod.module.css'
import { FormTitle } from '../../../../Common/FormTitle'
import { NextButton } from '../../../../Common/CtaButton/NextButton'
import { StepContext } from '../../../StepContainer'
import { handleNumericInput, validateEmploymentDates } from '../../EnrollmentPeriod/validate'
import { HandleYearChangeFunction } from '../../EnrollmentPeriod/types'
import { useAbTestGtm } from '../../../../../hooks/useAbTestGtm'
import { WorkPeriodFields } from './WorkPeriodFields'

export const JobSeekerWorkPeriodOnly = () => {
    const { dataClickLabel, dataViewLabel } = useAbTestGtm()
    const { formState } = useContext(StepContext)

    const [workStartYear, setWorkStartYear] = useState<string>(formState.userResumesAttributes[0]?.workStartYear || '')
    const [workStartMonth, setWorkStartMonth] = useState<string>(formState.userResumesAttributes[0]?.workStartMonth || '')
    const [workEndYear, setWorkEndYear] = useState<string | null>(formState.userResumesAttributes[0]?.workEndDateYear || null)
    const [workEndMonth, setWorkEndMonth] = useState<string | null>(formState.userResumesAttributes[0]?.workEndDateMonth || null)
    const [isTenure, setIsTenure] = useState<boolean>(formState.situation === 'tenure')
    const [hasInteractedPeriod, setHasInteractedPeriod] = useState<boolean>(false)
    const [startDateError, setStartDateError] = useState<string>('')
    const [endDateError, setEndDateError] = useState<string>('')
    const [isFormValid, setIsFormValid] = useState<boolean>(false)

    useEffect(() => {
        if (formState.situation === 'tenure') {
            setIsTenure(true)
            setWorkEndYear(null)
            setWorkEndMonth(null)
        } else {
            setIsTenure(false)
        }
    }, [formState.situation])

    const checkHasPeriodInput = (): boolean => {
        return (
            workStartYear !== '' ||
            workStartMonth !== '' ||
            (workEndYear !== null && workEndYear !== '') ||
            (workEndMonth !== null && workEndMonth !== '')
        )
    }

    useEffect(() => {
        if (checkHasPeriodInput() && !hasInteractedPeriod) {
            setHasInteractedPeriod(true)
        }
    }, [workStartYear, workStartMonth, workEndYear, workEndMonth, hasInteractedPeriod])

    useEffect(() => {
        const hasStartDateInput = workStartYear !== '' || workStartMonth !== ''
        const hasEndDateInput = workEndYear !== null && (workEndYear !== '' || (workEndMonth !== null && workEndMonth !== ''))
        const shouldValidatePeriod = hasInteractedPeriod || hasStartDateInput || (!isTenure && hasEndDateInput)

        if (shouldValidatePeriod) {
            const validationResult = validateEmploymentDates({
                workStartYear,
                workStartMonth,
                workEndYear,
                workEndMonth,
                isTenure,
            })

            setIsFormValid(validationResult.isValid)
            setStartDateError(validationResult.startDateError)
            setEndDateError(validationResult.endDateError)
        } else {
            setIsFormValid(false)
            setStartDateError('')
            setEndDateError('')
        }
    }, [
        workStartYear,
        workStartMonth,
        workEndYear,
        workEndMonth,
        isTenure,
        hasInteractedPeriod,
    ])

    const handleStartYearChange: HandleYearChangeFunction = value => {
        handleNumericInput(value, setWorkStartYear)
        if (!hasInteractedPeriod) setHasInteractedPeriod(true)
    }

    const handleEndYearChange: HandleYearChangeFunction = value => {
        handleNumericInput(value, setWorkEndYear)
        if (!hasInteractedPeriod) setHasInteractedPeriod(true)
    }

    const handleStartMonthChange = (value: string) => {
        setWorkStartMonth(value)
        if (!hasInteractedPeriod) setHasInteractedPeriod(true)
    }

    const handleEndMonthChange = (value: string) => {
        setWorkEndMonth(value)
        if (!hasInteractedPeriod) setHasInteractedPeriod(true)
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.selectionWrapper}>
                <div className={styles.monthbox}>
                    <FormTitle
                        formTitleText='在籍期間を教えてください'
                        dataViewLabel={dataViewLabel('EF_StepForm_在籍期間')}
                        dataClickLabel={dataClickLabel('EF_StepForm_在籍期間-戻る')}
                    />
                </div>
                <WorkPeriodFields
                    workStartYear={workStartYear}
                    workStartMonth={workStartMonth}
                    workEndYear={workEndYear}
                    workEndMonth={workEndMonth}
                    isTenure={isTenure}
                    hasInteractedPeriod={hasInteractedPeriod}
                    startDateError={startDateError}
                    endDateError={endDateError}
                    onStartYearChange={handleStartYearChange}
                    onStartMonthChange={handleStartMonthChange}
                    onEndYearChange={handleEndYearChange}
                    onEndMonthChange={handleEndMonthChange}
                />

                <NextButton
                    formKey={[
                        'userResumesAttributes',
                        'workStartYear',
                        'workStartMonth',
                        'workEndDateYear',
                        'workEndDateMonth',
                    ]}
                    formValue={[
                        0,
                        workStartYear,
                        workStartMonth,
                        workEndYear,
                        workEndMonth,
                    ]}
                    displayFormKey={['workStartDate', 'workEndDate']}
                    displayFormValue={[
                        `${workStartYear}年${workStartMonth}月`,
                        formState.situation === 'tenure'
                            ? '在職中'
                            : `${workEndYear}年${workEndMonth}月`,
                    ]}
                    isDisabled={!isFormValid}
                    dataClickLabel={dataClickLabel('EF_StepForm_在籍期間-次へ')}
                />
            </div>
        </div>
    )
}
